'use client';

import {useState} from 'react';
import Image from 'next/image';
import {formatDate} from '@/shared/functions/Functions';
import UserAvatar from './UserAvatar';
import { useTranslation } from 'react-i18next';

const truncate = (str: string | undefined | null, n: number) =>
    str && str.length > n ? `${str.slice(0, n)}…` : str ?? '';

interface Props {
    event: any;
}

export default function EventCard({event}: Props) {
    const [expanded, setExpanded] = useState(false);

    const offer = event?.offerItem?.offer;
    const platform = event?.offerItem?.socialPlatform;
    const totalPosts = event?.offerItem?.nbPosts;
    const currentPos = event?.platformPosition;
    const user = event?.user;
    const photo = user?.profilePhoto?.thumbnail || user?.profilePhoto?.path;
    const name = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim();
    const displayName = user?.displayName;
    const approvedByName = event?.approvedBy?.firstName + ' ' + event?.approvedBy?.lastName;

    const hashtags: string = event?.campaign?.hashtags ?? '';
    const profileTags: string = event?.campaign?.profileTags ?? '';
    const attachments: any[] = event?.attachments ?? [];
    const {t} = useTranslation();
    const hasDetails =
        event?.storyline || event?.caption || hashtags.length || profileTags.length || attachments.length;

    const imageSize = 45;

    return (
        <>
            <tr
                onClick={() => hasDetails && setExpanded(e => !e)}
                style={{cursor: hasDetails ? 'pointer' : 'default'}}
                className={`event-card ${expanded && 'box-shadow--no-bottom'}`}
            >
                <td className='text-nowrap'>
                    <div className='d-flex align-items-center'>
            <span
                className='mx-2 text-muted'
                style={{
                    display: 'inline-block',
                    width: 12,
                    transition: 'transform 0.15s',
                    transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                }}
            >
              {hasDetails ? '›' : ' '}
            </span>
                        <div className='d-flex align-items-center'>
                            <div className='mr-2'>
                                <UserAvatar src={photo} name={name} size={40} displayName={displayName}/>
                            </div>
                            <div className='d-flex flex-column'>
                                <span className='text-dark font-weight-semi-bold'>
                                    {truncate(name, 20)}
                                </span>
                                <div className='d-flex flex-wrap mt-1' style={{gap: 4}}>
                                <span className='text-muted'>
                                    {truncate(displayName, 20)}
                                 </span>
                                    {offer?.isLongTerm && (
                                        <span className='custom-badge long-term'>Long term</span>
                                    )}
                                    {offer?.isOnTop && (
                                        <span className='custom-badge on-top'>On top</span>
                                    )}
                                    {offer?.isAddonsOnly && (
                                        <span className='custom-badge additional'>Additional</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </td>
                <td className='text-center text-nowrap'>
                    {event?.postingDate ? formatDate(event.postingDate) : '-'}
                </td>
                <td className='text-center'>
                    <div className='d-flex align-items-center justify-content-center'>
                        {platform?.path && (
                            <Image
                                src={platform.path}
                                alt={platform.name}
                                width={36}
                                height={36}
                                style={{objectFit: 'contain'}}
                            />
                        )}
                        <span className='ml-2 text-muted'>
                            {t(`general.platforms.${platform.name}`)}
            </span>
                    </div>
                </td>
                <td className='text-center'>
          <span className={`status ${event?.status}`}>
            {event?.status?.replace('_', ' ')}
          </span>
                </td>

            </tr>
            {expanded && hasDetails ? (
                <tr
                    className={`event-card ${expanded && 'box-shadow--no-top'}`}>
                    <td colSpan={12} className='p-4 w-100'>
                        <div className='row'>
                            {/* Left column — meta */}
                            <div className='col-md-3'>
                                <div className='small text-muted mb-1'>Placement ID</div>
                                <div className='font-weight-bold mb-3'>#{event.id}</div>
                                <div className='small text-muted mb-1'>Hashtags:</div>
                                {hashtags?.length > 0 && (
                                    hashtags?.split(' ')?.map((item, i, arr) => (
                                        <span key={i} className={`font-weight-bold ${i > 0 ? 'pl-2' : ''}`}>
                                            {item}{i < arr.length - 1 ? ',' : ''}
                                        </span>
                                    ))
                                )}                                <div className='small text-muted mb-1 mt-3'>Tag:</div>
                                {profileTags?.length > 0 && (
                                    profileTags?.split(' ')?.map((item, i, arr) => (
                                        <span key={i} className={`font-weight-bold ${i > 0 ? 'pl-2' : ''}`}>
                                            {item}{i < arr.length - 1 ? ',' : ''}
                                        </span>
                                    ))
                                )}
                                {!!event.approvedBy &&
                                    <div className='mr-2'>
                                        <div className='mb-1 mt-3'>Approved by:</div>
                                        <div className="d-flex align-items-center">
                                            <UserAvatar src={event?.approvedBy?.profilePhoto?.path ?? ''} name={approvedByName} size={40}/>
                                            <p className="font-weight-bold mb-0 ml-2">{approvedByName}</p>
                                        </div>
                                    </div>
                                }
                            </div>

                            {/* Right column — content */}
                            <div className='col-md-9'>
                                <div className="d-flex">
                                        <div className='mb-3 w-50'>
                                            <div className='small text-muted mb-1'>Storyline</div>
                                            <div
                                                className='p-2 bg-white  border default-radius'
                                                style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}
                                            >
                                                {event.storyline ?? '-'}
                                            </div>
                                        </div>

                                        <div className={`mb-3 ml-3 w-50`}>
                                            <div className='small text-muted mb-1'>Caption</div>
                                            <div
                                                className='p-2 bg-white rounded border'
                                                style={{whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}
                                            >
                                                {event.caption ?? '-'}
                                            </div>
                                        </div>
                                </div>
                                    <div>
                                        <div className='small text-muted mb-2'>
                                            Content files ({attachments?.length ?? 0})
                                        </div>
                                        <div className='d-flex flex-wrap position-relative' style={{gap: 8}}>
                                            {attachments?.map((a, index) => (
                                                !!a?.externalLink ?
                                                    <a key={a.id}
                                                       href={a?.externalLink}
                                                       target='_blank'
                                                       rel='noopener noreferrer'
                                                       className='d-flex align-items-center p-2 bg-white border rounded text-decoration-none'
                                                       style={{minWidth: 220}}>
                                                        <div
                                                            className='mr-2 flex-shrink-0 d-flex align-items-center justify-content-center bg-light rounded'
                                                            style={{width: 48, height: 48}}>
                                                                <Image
                                                                    src="/images/videoPlaceholder.svg"
                                                                    alt="External link"
                                                                    width={imageSize}
                                                                    height={imageSize}
                                                                    style={{objectFit: 'cover'}}
                                                                />
                                                        </div>
                                                        <div className="w-100">
                                                            <div className="d-flex align-items-center justify-content-between mb-1 ">
                                                            <span className="font-weight-bold text-dark">File {index + 1}/{attachments?.length} {' '}</span>
                                                            {a?.isApproved &&
                                                                <span className="status approved text-dark ml-1 ">
                                                                 Approved
                                                                </span>
                                                            }
                                                            </div>
                                                            <p className="mb-0 cursor-pointer">External link</p>
                                                        </div>
                                                    </a>
                                                    :
                                                    <a key={a.id}
                                                       href={a.path}
                                                       target='_blank'
                                                       rel='noopener noreferrer'
                                                       className='d-flex align-items-center p-2 bg-white border rounded text-decoration-none'
                                                       style={{minWidth: 220}}>
                                                        <div
                                                            className='mr-2 flex-shrink-0 d-flex align-items-center justify-content-center bg-light rounded'
                                                            style={{width: 48, height: 48}}>
                                                            {(a.thumbnail || a.thumbPath) ? (
                                                                <Image
                                                                    src={a?.thumbPath ?? a?.thumbnail}
                                                                    alt={a.name}
                                                                    width={imageSize}
                                                                    height={imageSize}
                                                                    style={{objectFit: 'cover'}}
                                                                />
                                                            ) : (
                                                                <span className='text-muted small'>
                                                                    {a.mime?.split('/')[1]?.toUpperCase() || 'FILE'}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="w-100">
                                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <span className="font-weight-bold text-dark">File {index + 1}/{attachments?.length} {' '}</span>
                                                            {a?.isApproved &&
                                                                <span className="status approved text-dark ml-1">
                                                                 Approved
                                                                </span>
                                                            }
                                                            </div>
                                                            <div className='font-weight-semi-bold text-truncate'>
                                                                {/*{truncate(a.title || a.name, 20)}*/}
                                                                Open
                                                            </div>
                                                        </div>
                                                    </a>
                                            ))}
                                        </div>
                                    </div>
                            </div>
                        </div>
                    </td>
                </tr>
            ) : null}
            <tr style={{height: '15px'}}></tr>

        </>
    );
}
